import Vue from "vue";
/* global Keycloak */ // loaded via <script> tag in public/index.html
import { apiGateway } from "@/shared/apiGateway";

let instance;

/** Returns the current instance of the auth service */
export const getInstance = () => instance;

const globalRole = "admin";

/** Creates (or returns) the Keycloak-backed auth instance */
export const useKeycloak = (options) => {
  if (instance) return instance;

  const keycloak = new Keycloak({
    url: options.url,
    realm: options.realm,
    clientId: options.clientId
  });

  // The instance is a Vue object so authGuard can use $watch("loading", ...)
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        profile: undefined,
        error: null
      };
    },
    methods: {
      /** Returns the current access token, refreshing if it will expire within 30 seconds */
      async getTokenSilently() {
        await keycloak.updateToken(30);
        return keycloak.token;
      },
      /** Redirects the user to the Keycloak login page */
      loginWithRedirect() {
        keycloak.login();
      },
      /** Logs the user out and ends the Keycloak session */
      logout() {
        this.profile = undefined;
        keycloak.logout();
      },
      isAuthorized() {
        return this.profile !== undefined;
      },
      isInGlobalRole() {
        return this.profile.roles.includes(globalRole);
      },
      isInRole(roleToCheck) {
        return this.profile.roles.includes(roleToCheck);
      },
      async setProfile() {
        try {
          this.profile = await apiGateway.apiGatewayGET("api/identity/");
        } catch (error) {
          this.profile = undefined;
          if (error.response === undefined || error.response.status !== 404) {
            alert("service unavailable - please contact LiftX Consult");
            this.logout();
          }
        }
      }
    },
    async created() {
      try {
        const authenticated = await keycloak.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          checkLoginIframe: false,
          responseMode: "query"
        });
        this.isAuthenticated = authenticated;
        if (authenticated) {
          this.user = keycloak.tokenParsed;
          await this.setProfile();
          // Proactively refresh the token before it expires
          setInterval(() => keycloak.updateToken(30), 10000);
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    }
  });

  return instance;
};

export const KeycloakPlugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useKeycloak(options);
  }
};
